import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import Flex from "@/components/Flex";
import CustomTabs from "@/components/app/CustomTabs";
import ProviderGroupProfileDetailCard from "@/components/app/ProviderGroupProfileDetailCard";
import AppointmentTypeDrawer from "@/components/app/admin/provider-groups/AppointmentTypeDrawer";
import DepartmentDrawer from "@/components/app/admin/provider-groups/DepartmentDrawer";
import LocationsDrawer from "@/components/app/admin/provider-groups/LocationsDrawer";
import ProviderGroupDrawer from "@/components/app/admin/provider-groups/ProviderGroupDrawer";
import UpsertReferringAgencyDrawer from "@/components/app/admin/provider-groups/UpsertReferringAgencyDrawer";
import PayersToolbar from "./payers/components/PayersToolbar";
import { usePayersControls } from "./payers/hooks/use-payers-controls";
import { PAYER_VIEW_ROLES } from "./payers/payers-constant";
import { getPayerPermissions } from "./payers/payers-utils";
import { useRoleController } from "@/components/useRoleController";
import { PROVIDER_GROUP_LOCATION_FORM_INITIAL_VALUES } from "@/constants/ProviderLocationFromInitialValues";
import { getBaseRole } from "@/utils/auth-storage";
import { useDebounce } from "@/hooks/use-debounce";
import { useReduceDisclosure } from "@/hooks/use-reduce-disclosure";
import {
  DEFAULT_INS_LABEL_LIST,
  DEFAULT_UUID_NAME_LIST,
  roleFilter,
  transformMultiSelectFrontEndVal,
  unNecessaryFEOfficeHourTrans,
} from "@/utils/utils";
import PracticeSettingActions from "./components/PracticeSettingActions";
import { usePracticeSettingProfile } from "./hooks/use-practice-setting-profile";
import {
  AuditLogFilters,
  auditLogFiltersInitial,
  practiceSettingsData,
  PROFILE_DETAIL_CARD_TABS,
} from "./practice-setting-constant";

const PracticeSettingLayout = () => {
  const [stateFilter, setStateFilter] = useState<string[]>();

  const defPath = location.pathname?.split("/").pop();
  const defTab = defPath === "practice-settings" ? "profile" : (defPath ?? "");

  const [activeButton, setActiveButton] = useState<string>(defTab);

  useEffect(() => {
    setActiveButton(defTab);
  }, [defTab]);

  const { realm, profileData, onEditProviderGroupSubmit, onAddLocationSubmit } =
    usePracticeSettingProfile();

  const navigate = useNavigate();
  const [
    editProviderGroupFormDisclosure,
    locationsModalDisclosure,
    addDepartmentModalDisclosure,
    addAppoinmentTypeModalDisclosure,
    addReferringAgencyModalDisclosure,
  ] = useReduceDisclosure(5);

  const [searchKey, setSearchKey, debounceVal] = useDebounce<string>("");
  const [auditLogFilters, setAuditLogFilters] = useState<AuditLogFilters>(
    auditLogFiltersInitial
  );
  const [showArchivedAppointmentType, setShowArchivedAppointmentType] =
    useState<boolean>(false);
  const [showArchivedReferringAgency, setShowArchivedReferringAgency] =
    useState<boolean>(false);
  const payersControls = usePayersControls();
  const { hasNone: hasAuditLogNone } = useRoleController("Settings Audit Log");

  return (
    <Flex flexDirection={"column"} height={"100%"}>
      <Flex justifyContent={"space-between"} flexWrap={"wrap"}>
        <CustomTabs
          tabsData={roleFilter({
            list: practiceSettingsData,
            compWith: {
              audit_log: !hasAuditLogNone,
              payers: PAYER_VIEW_ROLES.includes(getBaseRole() ?? ""),
            },
          })}
          value={activeButton}
          onChange={(v) => {
            setStateFilter(undefined);
            setSearchKey("");
            navigate(
              practiceSettingsData.find((val) => val.value === v)?.path ??
                practiceSettingsData[0].path
            );

            setActiveButton(v);
          }}
        />
        {activeButton === "payers" ? (
          <PayersToolbar
            controls={payersControls}
            canAdd={getPayerPermissions().canAdd}
          />
        ) : (
          <PracticeSettingActions
            activeButton={activeButton}
            searchKey={debounceVal}
            onSearch={setSearchKey}
            stateFilter={stateFilter}
            onStateFilterChange={setStateFilter}
            showArchivedAppointmentType={showArchivedAppointmentType}
            onShowArchivedAppointmentTypeChange={setShowArchivedAppointmentType}
            showArchivedReferringAgency={showArchivedReferringAgency}
            onShowArchivedReferringAgencyChange={setShowArchivedReferringAgency}
            auditLogFilters={auditLogFilters}
            setAuditLogFilters={setAuditLogFilters}
            onEditProfile={editProviderGroupFormDisclosure.open}
            onAddLocation={locationsModalDisclosure.open}
            onAddDepartment={addDepartmentModalDisclosure.open}
            onAddReferringAgency={addReferringAgencyModalDisclosure.open}
            onAddAppointmentType={addAppoinmentTypeModalDisclosure.open}
          />
        )}
      </Flex>

      {locationsModalDisclosure.opened && (
        <LocationsDrawer
          providerGroupTimezone={profileData?.timezone}
          shouldGetPrefillList
          prefillSpecialtyList={transformMultiSelectFrontEndVal(
            DEFAULT_UUID_NAME_LIST,
            profileData?.specialities
          )}
          preFillValues={{
            ...PROVIDER_GROUP_LOCATION_FORM_INITIAL_VALUES,
            timezone: profileData?.timezone,
            officeHours: unNecessaryFEOfficeHourTrans(profileData?.officeHours),
          }}
          open={locationsModalDisclosure.opened}
          handleCloseModal={locationsModalDisclosure.close}
          title="Add Location"
          onSubmit={onAddLocationSubmit}
        />
      )}

      {addDepartmentModalDisclosure.opened && (
        <DepartmentDrawer
          open={addDepartmentModalDisclosure.opened}
          modalTitle={"Add Department"}
          onClose={addDepartmentModalDisclosure.close}
          xTENANTID={realm ?? undefined}
        />
      )}

      {addReferringAgencyModalDisclosure.opened && (
        <UpsertReferringAgencyDrawer
          open={addReferringAgencyModalDisclosure.opened}
          onClose={addReferringAgencyModalDisclosure.close}
        />
      )}

      {addAppoinmentTypeModalDisclosure.opened && (
        <AppointmentTypeDrawer
          disclosureHost={addAppoinmentTypeModalDisclosure}
          modalTitle={"Add Appointment Type"}
        />
      )}

      {editProviderGroupFormDisclosure.opened && (
        <ProviderGroupDrawer
          openProviderForm={editProviderGroupFormDisclosure.opened}
          handleClose={editProviderGroupFormDisclosure.close}
          title="Edit Provider Group Profile"
          onSubmit={onEditProviderGroupSubmit}
          prefillValues={
            {
              ...profileData,
              officeHours: unNecessaryFEOfficeHourTrans(
                profileData?.officeHours
              ),
              acceptedInsurances: transformMultiSelectFrontEndVal(
                DEFAULT_INS_LABEL_LIST,
                profileData?.acceptedInsurances as any
              ),
              specialities: transformMultiSelectFrontEndVal(
                DEFAULT_UUID_NAME_LIST,
                profileData?.specialities as any
              ),
            } as any
          }
        />
      )}

      {PROFILE_DETAIL_CARD_TABS.includes(activeButton) && (
        <Box marginY={"20px"}>
          <ProviderGroupProfileDetailCard profileData={profileData} />
        </Box>
      )}

      <Box flex={1}>
        <Outlet
          context={{
            searchKey,
            onClearSearch: () => setSearchKey(""),
            stateFilter,
            groupNpi: profileData?.npi,
            profileData,
            pgName: profileData?.name,
            auditLogFilters,
            showArchivedAppointmentType,
            showArchivedReferringAgency,
            payersControls,
          }}
        />
      </Box>
    </Flex>
  );
};

export default PracticeSettingLayout;
